'use client'

import { useEffect, useState } from 'react'
import { X, Download } from 'lucide-react'
import { useMessages } from '@/lib/i18n/client'
import { useConsentDecided } from '@/lib/tracking/consent'
import messages from './android-app-banner.i18n'

const STORAGE_KEY = 'android_banner_dismissed'
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=cl.ciudadviva.app&pcampaignid=web_share'

function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent)
}

// Banner no bloqueante que se muestra en la parte inferior de la pantalla
// cuando el usuario visita la web desde Android. Solo aparece una vez: tras
// cerrarlo se guarda la preferencia en localStorage para no volver a molestarlo.
// No se muestra en iOS ni en desktop.
export function AndroidAppBanner() {
  const [visible, setVisible] = useState(false)
  const t = useMessages(messages)
  // Espera a que se responda el aviso de cookies (no apilar avisos abajo).
  const consentDecided = useConsentDecided()

  useEffect(() => {
    if (!isAndroid()) return
    if (localStorage.getItem(STORAGE_KEY)) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisible(true)
  }, [])

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, '1')
    setVisible(false)
  }

  if (!visible || !consentDecided) return null

  return (
    <div
      role="banner"
      aria-label={t.label}
      className="fixed bottom-0 left-0 right-0 z-[1100] flex items-center gap-3 bg-background/95 px-4 py-3 shadow-[0_-2px_16px_rgba(0,0,0,0.18)] backdrop-blur-sm border-t border-border"
    >
      {/* Ícono / logo */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
        <Download className="h-5 w-5" />
      </div>

      {/* Texto */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight text-foreground">
          {t.title}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
          {t.body}
        </p>
      </div>

      {/* CTA */}
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={dismiss}
        className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90 active:scale-95"
      >
        {t.download}
      </a>

      {/* Cerrar */}
      <button
        type="button"
        onClick={dismiss}
        aria-label={t.close}
        className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
